import { useDebounceFn } from '@vueuse/core'

// Persistence is fire-and-forget, once per drag and once per keyboard step, so
// this absorbs a held arrow key. It never gates the live, on-pointermove resize.
const PERSIST_DEBOUNCE_MS = 150
const PANE_ID = 'main-content-pane'
const MIN_PANE_WIDTH_PX = 320

export interface DrawerWidthLayout {
  minWidthPx: number
  maxWidthPx: number
  // Returns the clamped value it applied so callers can track it without
  // re-measuring the DOM.
  setWidthPx: (px: number, options: { persist: boolean }) => number
  // Current drawer width in pixels, or null while the layout is absent.
  readWidthPx: () => number | null
  requestedWidthPx: () => number | null
  maxAvailableWidthPx: () => number | null
  // Snapshots the drag geometry, the drawer's anchored edge, which is stable
  // while a drag lasts. The returned converter is then pure math, with no
  // layout reads on pointermove.
  beginDragMeasurement: () => ((clientX: number) => number) | null
}

interface DrawerWidthLayoutConfig {
  drawerId: string
  cssVariable: string
  minWidthPx: number
  maxWidthPx: number
  anchoredEdge: 'left' | 'right'
  persist: (px: number) => Promise<void>
}

// Stimulus-to-Slim boundary for a resizable drawer: element lookups against the
// server-rendered layout live here. The drawer is sized in pixels via a CSS
// variable on `<body>`, server-rendered inline from the persisted preference,
// overwritten by `setWidthPx` in-session, and falling back to the responsive
// default in `two-drawer-layout-new.scss` when unset. It belongs on `<body>`
// because docked persistent modals render outside `#main-wrapper` and derive
// their offset from the same resolved width.
export function createDrawerWidthLayout({
  drawerId,
  cssVariable,
  minWidthPx,
  maxWidthPx,
  anchoredEdge,
  persist,
}: DrawerWidthLayoutConfig): DrawerWidthLayout {
  const getDrawer = (): HTMLElement | null => document.getElementById(drawerId)

  const clampPx = (px: number): number =>
    Math.min(maxWidthPx, Math.max(minWidthPx, Math.round(px)))

  const schedulePersist = useDebounceFn((px: number) => {
    void persist(px)
  }, PERSIST_DEBOUNCE_MS)

  const maxAvailableWidthPx = (): number | null => {
    const drawer = getDrawer()
    const pane = document.getElementById(PANE_ID)
    if (!drawer || !pane) return null
    return Math.round(
      drawer.getBoundingClientRect().width +
        pane.getBoundingClientRect().width -
        MIN_PANE_WIDTH_PX
    )
  }

  return {
    minWidthPx,
    maxWidthPx,

    setWidthPx(px: number, options: { persist: boolean }): number {
      const clamped = clampPx(px)
      document.body.style.setProperty(cssVariable, `${clamped}px`)
      if (options.persist) void schedulePersist(clamped)
      return clamped
    },

    readWidthPx(): number | null {
      const drawer = getDrawer()
      if (!drawer) return null
      return Math.round(drawer.getBoundingClientRect().width)
    },

    requestedWidthPx(): number | null {
      const value = parseInt(
        document.body.style.getPropertyValue(cssVariable),
        10
      )
      return Number.isNaN(value) ? null : value
    },

    maxAvailableWidthPx,

    beginDragMeasurement(): ((clientX: number) => number) | null {
      const drawer = getDrawer()
      if (!drawer) return null
      const rect = drawer.getBoundingClientRect()
      const available = maxAvailableWidthPx() ?? Infinity
      return anchoredEdge === 'left'
        ? (clientX: number): number => Math.min(available, clientX - rect.left)
        : (clientX: number): number => Math.min(available, rect.right - clientX)
    },
  }
}
